import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { t } from '../lib/i18n';
import type { MessageKey } from '../locales/en';

export type ThemeName = 'dark' | 'light';

export interface SettingsState {
  theme: ThemeName;
  /** 'local', 'UTC' or an IANA zone. */
  timeZone: string;
  /** Faint symbol + interval behind the candles. */
  watermark: boolean;
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
  setTimeZone: (tz: string) => void;
  setWatermark: (on: boolean) => void;
}

export const useSettings = create<SettingsState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      timeZone: 'local',
      watermark: true,
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set({ theme: get().theme === 'dark' ? 'light' : 'dark' }),
      setTimeZone: (timeZone) => set({ timeZone }),
      setWatermark: (watermark) => set({ watermark }),
    }),
    { name: 'ch:settings' },
  ),
);

/** Keep the `dark` class on <html> in step with the theme setting. */
export function applyTheme(theme: ThemeName): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', theme === 'dark' ? '#0b1011' : '#ffffff');
}

/** A time zone's name in the page language (the city, "UTC", or "Local time"). */
export function timeZoneLabel(zone: (typeof TIME_ZONES)[number]): string {
  if (zone.id === 'local') return t('settings.localTime');
  return zone.key ? t(zone.key) : zone.label;
}

export const TIME_ZONES: Array<{ id: string; label: string; key?: MessageKey }> = [
  { id: 'local', label: 'Local time' },
  { id: 'UTC', label: 'UTC' },
  { id: 'Europe/London', label: 'London', key: 'tz.london' },
  { id: 'Europe/Amsterdam', label: 'Amsterdam', key: 'tz.amsterdam' },
  { id: 'Europe/Istanbul', label: 'Istanbul', key: 'tz.istanbul' },
  { id: 'Asia/Dubai', label: 'Dubai', key: 'tz.dubai' },
  { id: 'Asia/Kolkata', label: 'Mumbai', key: 'tz.mumbai' },
  { id: 'Asia/Singapore', label: 'Singapore', key: 'tz.singapore' },
  { id: 'Asia/Hong_Kong', label: 'Hong Kong', key: 'tz.hongKong' },
  { id: 'Asia/Tokyo', label: 'Tokyo', key: 'tz.tokyo' },
  { id: 'Australia/Sydney', label: 'Sydney', key: 'tz.sydney' },
  { id: 'America/New_York', label: 'New York', key: 'tz.newYork' },
  { id: 'America/Chicago', label: 'Chicago', key: 'tz.chicago' },
  { id: 'America/Los_Angeles', label: 'Los Angeles', key: 'tz.losAngeles' },
  { id: 'America/Sao_Paulo', label: 'São Paulo', key: 'tz.saoPaulo' },
];
